// Iter 31.287 — spyacademy.uk (Two's brief, 09 Jul 2026).
//
// Deliberately breaks from the Village aesthetic:
//   · pitch green + warm off-white + charcoal + sparing gold accent
//   · tight grotesk type (Space Grotesk, negative tracking)
//   · six-pillar FIFA/UEFA institutional layout, hero + pillar grid
//   · documentary photography over illustration
//
// Reads to a general audience that has never heard of Two or the
// Village. No cursor blinks, no stamps, no "//" comment styling.
//
// Content flows from `spyacademy_pages` via the generic micro-CMS
// endpoint. Pillar copy is pulled from a page slug convention
// (`pillar-<n>`); if missing, sensible defaults render. Two can
// override each pillar via `update_site_page` MCP tool.

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_ID = "spyacademy";
const DOMAIN = "spyacademy.uk";
const SPA_PREFIX = "/spyacademy";

const REHYPE_PLUGINS = [rehypeRaw];

// Route prefix: strip on masked-domain visits, keep on spyfinance.cloud.
function _routePrefix() {
    if (typeof window === "undefined") return SPA_PREFIX;
    return window.location.hostname === DOMAIN ? "" : SPA_PREFIX;
}

// Documentary photography — Unsplash sources, muted, monochromatic.
// Two curates the final set; these are placeholders that read straight
// on first paint. Each pillar has one photograph. The images share a
// desaturated / high-contrast documentary treatment.
const DEFAULT_PILLARS = [
    {
        slug: "pillar-1",
        no: "01",
        title: "Craft",
        strap: "The materials of a discipline, learnt in the hand before the head.",
        photo: "https://images.unsplash.com/photo-1533628635777-112b2239b1c7?w=1200&q=80&auto=format&fit=crop",
    },
    {
        slug: "pillar-2",
        no: "02",
        title: "Attention",
        strap: "The rarest of resources — spent, not spread.",
        photo: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=80&auto=format&fit=crop",
    },
    {
        slug: "pillar-3",
        no: "03",
        title: "Apprenticeship",
        strap: "Learning by proximity. The old model, still the best.",
        photo: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80&auto=format&fit=crop",
    },
    {
        slug: "pillar-4",
        no: "04",
        title: "Mentorship",
        strap: "The debt of the taught is to teach.",
        photo: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1200&q=80&auto=format&fit=crop",
    },
    {
        slug: "pillar-5",
        no: "05",
        title: "Quiet Expertise",
        strap: "The knowing that doesn't need to announce itself.",
        photo: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80&auto=format&fit=crop",
    },
    {
        slug: "pillar-6",
        no: "06",
        title: "Practice",
        strap: "Repetition, refined. The only path there is.",
        photo: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80&auto=format&fit=crop",
    },
];

// Reusable palette — declared once, applied via inline style on the
// outermost wrapper so nothing leaks into the rest of the SPA.
const PALETTE = {
    pitch: "#0e3a2a",       // pitch green — dominant surface
    pitchDeep: "#0a2b1f",   // for hover / stronger contrast
    offwhite: "#f5f1e6",    // warm off-white — body copy on pitch
    parchment: "#eae3d3",   // slightly deeper cream for cards
    charcoal: "#1a1a1a",    // for text on off-white surfaces
    gold: "#c8a24a",        // sparing accent — links / eyebrows only
    goldMuted: "#8a6f2f",
    line: "rgba(245,241,230,0.14)",
};

// Iter 31.292 — Google font: Lexend at weight 500 (Six's brief).
// Idempotent — repeated inserts are no-ops.
function _ensureFontLoaded() {
    if (typeof document === "undefined") return;
    const id = "spyacademy-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
}

// ── Small text helpers ───────────────────────────────────────────────
const Eyebrow = ({ children, style }) => (
    <div
        style={{
            fontFamily: "'Lexend', system-ui, sans-serif",
            fontSize: 11,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            fontWeight: 500,
            color: PALETTE.gold,
            ...style,
        }}
    >
        {children}
    </div>
);

const Rule = ({ style }) => (
    <div
        style={{
            height: 1,
            background: PALETTE.line,
            width: "100%",
            ...style,
        }}
    />
);

// ── HERO ─────────────────────────────────────────────────────────────
function Hero({ index }) {
    const strap = (index?.body || "").split("\n\n")[0]
        || "An academy of quiet expertise. Six disciplines, one standard, taught in the hand and the head. Documentary in method, patient in delivery.";
    return (
        <section
            style={{
                background: PALETTE.pitch,
                color: PALETTE.offwhite,
                paddingTop: "10vh",
                paddingBottom: "10vh",
                borderBottom: `1px solid ${PALETTE.line}`,
            }}
            data-testid="spyacademy-hero"
        >
            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 6vw" }}>
                <Eyebrow>Spy Academy · est. 2026</Eyebrow>
                <h1
                    style={{
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: "clamp(48px, 8vw, 128px)",
                        fontWeight: 700,
                        letterSpacing: "-0.035em",
                        lineHeight: 0.95,
                        margin: "24px 0 0 0",
                        maxWidth: "18ch",
                    }}
                >
                    {index?.title || "The quiet study of craft."}
                </h1>
                <p
                    style={{
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: "clamp(16px, 1.4vw, 20px)",
                        fontWeight: 400,
                        letterSpacing: "-0.005em",
                        lineHeight: 1.5,
                        marginTop: 40,
                        maxWidth: "48ch",
                        color: "rgba(245,241,230,0.82)",
                    }}
                    data-testid="spyacademy-hero-strap"
                >
                    {strap}
                </p>
            </div>
        </section>
    );
}

// ── SIX-PILLAR GRID ──────────────────────────────────────────────────
function PillarCard({ pillar }) {
    const [hover, setHover] = useState(false);
    return (
        <Link
            to={`${_routePrefix()}/${pillar.slug}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: "block",
                textDecoration: "none",
                color: "inherit",
                background: PALETTE.pitchDeep,
                border: `1px solid ${PALETTE.line}`,
                overflow: "hidden",
                transition: "transform 380ms cubic-bezier(0.2, 0.7, 0.2, 1), border-color 380ms",
                transform: hover ? "translateY(-4px)" : "translateY(0)",
                borderColor: hover ? PALETTE.gold : PALETTE.line,
            }}
            data-testid={`spyacademy-pillar-${pillar.slug}`}
        >
            <div
                style={{
                    aspectRatio: "4 / 3",
                    background: `url(${pillar.photo}) center/cover no-repeat`,
                    filter: "saturate(0.65) contrast(1.05)",
                    borderBottom: `1px solid ${PALETTE.line}`,
                    position: "relative",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(180deg, rgba(10,43,31,0) 40%, rgba(10,43,31,0.55) 100%)",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        top: 16,
                        left: 20,
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: 11,
                        letterSpacing: "0.24em",
                        color: PALETTE.gold,
                        fontWeight: 500,
                    }}
                >
                    {pillar.no}
                </div>
            </div>
            <div style={{ padding: "28px 24px 32px" }}>
                <h2
                    style={{
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: 30,
                        fontWeight: 600,
                        letterSpacing: "-0.02em",
                        lineHeight: 1.05,
                        margin: 0,
                        color: PALETTE.offwhite,
                    }}
                >
                    {pillar.title}
                </h2>
                <p
                    style={{
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: 14,
                        lineHeight: 1.55,
                        letterSpacing: "-0.003em",
                        marginTop: 12,
                        color: "rgba(245,241,230,0.68)",
                    }}
                >
                    {pillar.strap}
                </p>
            </div>
        </Link>
    );
}

function PillarGrid({ pillars }) {
    return (
        <section
            style={{
                background: PALETTE.pitch,
                paddingTop: "8vh",
                paddingBottom: "10vh",
                borderBottom: `1px solid ${PALETTE.line}`,
            }}
            data-testid="spyacademy-pillars"
        >
            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 6vw" }}>
                <Eyebrow>The six pillars</Eyebrow>
                <Rule style={{ marginTop: 20, marginBottom: 40 }} />
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                        gap: 20,
                    }}
                >
                    {pillars.map((p) => (
                        <PillarCard key={p.slug} pillar={p} />
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── FOOTER ───────────────────────────────────────────────────────────
function Footer() {
    return (
        <footer
            style={{
                background: PALETTE.pitchDeep,
                color: "rgba(245,241,230,0.55)",
                padding: "40px 6vw",
                fontFamily: "'Lexend', system-ui, sans-serif",
                fontSize: 12,
                letterSpacing: "0.05em",
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 20,
            }}
            data-testid="spyacademy-footer"
        >
            <span>Spy Academy · {new Date().getFullYear()}</span>
            <span style={{ color: PALETTE.goldMuted }}>{DOMAIN}</span>
        </footer>
    );
}

// ── INDEX PAGE (six-pillar hero) ─────────────────────────────────────
export function SpyAcademyIndex() {
    const [index, setIndex] = useState(null);
    const [pillars, setPillars] = useState(DEFAULT_PILLARS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        _ensureFontLoaded();
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [idxRes, ...pillarRes] = await Promise.allSettled([
                    axios.get(`${API}/api/sites/${SITE_ID}/index`),
                    ...DEFAULT_PILLARS.map((p) =>
                        axios.get(`${API}/api/sites/${SITE_ID}/pages/${p.slug}`),
                    ),
                ]);
                if (cancelled) return;
                if (idxRes.status === "fulfilled") setIndex(idxRes.value.data);
                // Merge each pillar override onto its default.
                const merged = DEFAULT_PILLARS.map((p, i) => {
                    const res = pillarRes[i];
                    if (res?.status !== "fulfilled") return p;
                    const data = res.value.data || {};
                    return {
                        ...p,
                        title: data.title || p.title,
                        strap: (data.body || "").split("\n\n")[0] || p.strap,
                        photo: data.cover_url || p.photo,
                    };
                });
                setPillars(merged);
            } catch {
                // fallthrough to defaults
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Suppress a flash of raw text on first paint by early-returning
    // a solid pitch surface while the network settles.
    if (loading) {
        return (
            <div
                style={{ background: PALETTE.pitch, minHeight: "100vh" }}
                data-testid="spyacademy-loading"
            />
        );
    }

    return (
        <div
            style={{
                background: PALETTE.pitch,
                minHeight: "100vh",
                fontFamily: "'Lexend', system-ui, sans-serif",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
            }}
            data-testid="spyacademy-index"
        >
            <Hero index={index} />
            <PillarGrid pillars={pillars} />
            <Footer />
        </div>
    );
}

// ── PILLAR / ARTICLE PAGE ────────────────────────────────────────────
export function SpyAcademyPage() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        _ensureFontLoaded();
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setNotFound(false);
            try {
                const { data } = await axios.get(
                    `${API}/api/sites/${SITE_ID}/pages/${slug}`,
                );
                if (cancelled) return;
                setPage(data);
            } catch (err) {
                if (!cancelled) setNotFound(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (loading) {
        return (
            <div
                style={{ background: PALETTE.pitch, minHeight: "100vh" }}
                data-testid="spyacademy-page-loading"
            />
        );
    }
    if (notFound) {
        // Fall back to the default pillar copy if it matches — Two hasn't
        // filed content for this slug yet, but the pillar strapline is
        // known. Keeps the site coherent while awaiting Two's editorial.
        const p = DEFAULT_PILLARS.find((x) => x.slug === slug);
        if (!p) {
            return (
                <div
                    style={{ background: PALETTE.pitch, color: PALETTE.offwhite, minHeight: "100vh", padding: "12vh 6vw", fontFamily: "'Lexend', system-ui, sans-serif" }}
                    data-testid="spyacademy-page-404"
                >
                    <Eyebrow>404</Eyebrow>
                    <h1 style={{ fontSize: 64, marginTop: 20, letterSpacing: "-0.03em" }}>
                        This page is not yet written.
                    </h1>
                    <Link to={_routePrefix() || "/"} style={{ color: PALETTE.gold, textDecoration: "none", marginTop: 32, display: "inline-block" }}>
                        ← Back to the Academy
                    </Link>
                </div>
            );
        }
        return <SpyAcademyPillarShell pillar={p} body={null} />;
    }
    return <SpyAcademyPillarShell pillar={{
        no: "",
        title: page.title,
        strap: (page.body || "").split("\n\n")[0],
        photo: page.cover_url || "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1600&q=80&auto=format&fit=crop",
    }} body={page.body} />;
}

function SpyAcademyPillarShell({ pillar, body }) {
    return (
        <div
            style={{
                background: PALETTE.pitch,
                minHeight: "100vh",
                fontFamily: "'Lexend', system-ui, sans-serif",
                color: PALETTE.offwhite,
            }}
            data-testid="spyacademy-page"
        >
            {/* Hero band */}
            <section
                style={{
                    background: PALETTE.pitchDeep,
                    borderBottom: `1px solid ${PALETTE.line}`,
                    padding: "8vh 6vw 6vh",
                }}
            >
                <div style={{ maxWidth: 1000, margin: "0 auto" }}>
                    <Link
                        to={_routePrefix() || "/"}
                        style={{
                            color: PALETTE.gold,
                            textDecoration: "none",
                            fontSize: 12,
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                        }}
                        data-testid="spyacademy-back-link"
                    >
                        ← Spy Academy
                    </Link>
                    {pillar.no && <Eyebrow style={{ marginTop: 32 }}>{pillar.no}</Eyebrow>}
                    <h1
                        style={{
                            fontFamily: "'Lexend', system-ui, sans-serif",
                            fontSize: "clamp(40px, 6vw, 88px)",
                            fontWeight: 700,
                            letterSpacing: "-0.03em",
                            lineHeight: 1.0,
                            margin: pillar.no ? "20px 0 0" : "32px 0 0",
                        }}
                    >
                        {pillar.title}
                    </h1>
                    <p
                        style={{
                            fontSize: 20,
                            lineHeight: 1.5,
                            letterSpacing: "-0.005em",
                            marginTop: 24,
                            color: "rgba(245,241,230,0.82)",
                            maxWidth: "48ch",
                        }}
                    >
                        {pillar.strap}
                    </p>
                </div>
            </section>
            {/* Documentary photograph */}
            <div
                style={{
                    aspectRatio: "16 / 7",
                    background: `url(${pillar.photo}) center/cover no-repeat`,
                    filter: "saturate(0.65) contrast(1.05)",
                    borderBottom: `1px solid ${PALETTE.line}`,
                }}
            />
            {/* Body */}
            {body && (
                <article
                    style={{
                        maxWidth: 720,
                        margin: "0 auto",
                        padding: "8vh 6vw",
                        background: PALETTE.parchment,
                        color: PALETTE.charcoal,
                    }}
                    data-testid="spyacademy-page-body"
                >
                    <div
                        style={{
                            fontFamily: "'Lexend', system-ui, sans-serif",
                            fontSize: 17,
                            lineHeight: 1.7,
                            letterSpacing: "-0.005em",
                        }}
                    >
                        <ReactMarkdown rehypePlugins={REHYPE_PLUGINS}>
                            {body}
                        </ReactMarkdown>
                    </div>
                </article>
            )}
            <Footer />
        </div>
    );
}
